import React, {useEffect, useState} from 'react';
import {fetchRef} from '../../../service/refsCache';
import Spinner from '../../Spinner';
import {choiceCardClass} from '../constants';
import {StepProps, asRecord, listField, stringField} from './types';

interface RefDto {
    id: string;
    code: string;
    nameRu: string;
    nameEn?: string;
}

/**
 * Шаг «Ваш стиль» (слот 6). Стили берём из справочника продукта (`GET /api/refs/styles`,
 * тот же `fetchRef('styles')`, что в настройках профиля) — выбранные значения при завершении
 * мастера уезжают в `UserSettings.preferredStyles`, а «стилевая цель» — в стратегию гардероба.
 */
const StepStyle: React.FC<StepProps> = ({value, onChange}) => {
    const [styles, setStyles] = useState<RefDto[]>([]);
    const [loading, setLoading] = useState(true);
    const selectedIds = listField(value, 'styleIds');
    const styleTarget = stringField(value, 'styleTarget');

    useEffect(() => {
        let cancelled = false;
        fetchRef<RefDto[]>('styles')
            .then(data => {
                if (!cancelled) {
                    setStyles(data);
                }
            })
            .catch(() => undefined)
            .finally(() => {
                if (!cancelled) {
                    setLoading(false);
                }
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const emit = (ids: string[], target: string) => {
        const resolved = ids
            .map(id => styles.find(s => s.id === id)?.nameRu)
            .filter((name): name is string => Boolean(name));
        // Справочник мог ещё не загрузиться (ввод цели сразу после открытия шага) — тогда
        // сохранённые названия важнее пустого результата: иначе они потерялись бы.
        const names = resolved.length === ids.length ? resolved : listField(value, 'styleNames');
        const next: Record<string, unknown> = {...asRecord(value), styleIds: ids, styleNames: names};
        if (target.trim()) {
            next.styleTarget = target;
        } else {
            delete next.styleTarget;
        }
        onChange(next);
    };

    const toggle = (id: string) => {
        const next = selectedIds.includes(id)
            ? selectedIds.filter(s => s !== id)
            : [...selectedIds, id];
        emit(next, styleTarget);
    };

    return (
        <div className="space-y-5" data-testid="onboarding-step-style">
            {loading && <Spinner/>}
            {!loading && styles.length === 0 && (
                <p className="text-sm text-gray-500 dark:text-gray-400" data-testid="onboarding-styles-unavailable">
                    Справочник стилей сейчас недоступен — опишите стиль словами ниже или пропустите шаг.
                </p>
            )}
            {!loading && styles.length > 0 && (
                <div className="flex flex-wrap gap-2" data-testid="onboarding-style-options">
                    {styles.map(s => (
                        <button
                            key={s.id}
                            type="button"
                            onClick={() => toggle(s.id)}
                            aria-pressed={selectedIds.includes(s.id)}
                            className={choiceCardClass(selectedIds.includes(s.id))}
                            data-testid={`onboarding-style-${s.code}`}
                        >
                            {s.nameRu}
                        </button>
                    ))}
                </div>
            )}
            <label className="flex flex-col gap-1">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                    Стилевая цель — к чему хотите прийти
                </span>
                <input
                    type="text"
                    maxLength={255}
                    value={styleTarget}
                    onChange={e => emit(selectedIds, e.target.value)}
                    className="input-field min-h-[44px]"
                    placeholder="Спокойная база в тёмных тонах, без лишнего"
                    data-testid="onboarding-style-target"
                />
            </label>
        </div>
    );
};

export default StepStyle;
